/**
 * Utility class for JWT token operations
 */
export class JwtUtils {
  /**
   * Decodes a JWT token and returns the payload
   * @param token - The JWT token to decode
   * @returns The decoded payload or null if invalid
   */
  static decodeToken(token: string): any | null {
    try {
      if (!token) {
        return null;
      }

      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      
      if (parts.length !== 3) {
        console.error('Invalid JWT token format');
        return null;
      }

      // The payload is the second part (index 1)
      const payload = parts[1];
      
      // JWT uses base64url encoding, but atob expects regular base64
      // Replace URL-safe characters and add padding if needed
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
      
      // Decode base64 and parse JSON
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Checks if a JWT token is expired
   * @param token - The JWT token to check
   * @returns true if expired, false otherwise
   */
  static isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.exp) {
      return true;
    }

    // JWT exp is in seconds, Date.now() is in milliseconds
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  /**
   * Gets the expiration date of a JWT token
   * @param token - The JWT token
   * @returns Date object or null if invalid
   */
  static getTokenExpirationDate(token: string): Date | null {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.exp) {
      return null;
    }

    // Convert seconds to milliseconds
    return new Date(payload.exp * 1000);
  }

  /**
   * Gets the user information from the JWT token
   * @param token - The JWT token
   * @returns User info object or null if invalid
   */
  static getUserFromToken(token: string): any | null {
    const payload = this.decodeToken(token);
    
    if (!payload) {
      return null;
    }

    // Extract common user fields from JWT payload
    return {
      id: payload.sub || payload.id || payload.user_id,
      email: payload.email,
      roles: payload.roles || [],
      permissions: payload.permissions || [],
      username: payload.username || payload.preferred_username,
      name: payload.name,
      exp: payload.exp,
      iat: payload.iat
    };
  }
}
